/**
 * @fileoverview Enhanced Activity Tracking Utility for comprehensive user behavior monitoring
 *
 * This module provides a sophisticated activity tracking system that monitors user interactions,
 * page navigation, clicks, form submissions, API calls, and system events. It's designed for
 * CPA applications requiring detailed audit trails and user behavior analysis.
 *
 * Key Features:
 * - Real-time user interaction tracking (clicks, scrolls, form submissions)
 * - Page navigation monitoring with duplicate detection
 * - Performance metrics collection
 * - Batch processing for efficient server communication
 * - Device and browser information capture
 * - Privacy-aware data sanitization
 * - Robust error handling and retry logic
 * - Session-based activity grouping
 *
 * Dependencies:
 * - Modern browser APIs (Performance, Fetch, MutationObserver)
 * - Server-side endpoint for activity data storage
 * - CSRF token support for secure communication
 *
 * @author Clockwise CPA Development Team
 * @version 1.0.0
 * @since 2024-01-01
 */

/**
 * Enhanced Activity Tracking Utility
 * Comprehensive tracking for user interactions, page navigation, clicks, and system events
 *
 * @class ActivityTracker
 * @example
 * const tracker = new ActivityTracker({
 *   apiEndpoint: '/api/activity-logs',
 *   enabled: true,
 *   batchSize: 10,
 *   batchInterval: 30000
 * });
 *
 * tracker.setUser(userId, organizationId);
 * tracker.trackPageView();
 */
class ActivityTracker {
    /**
     * Creates a new ActivityTracker instance
     *
     * @param {Object} options - Configuration options for the tracker
     * @param {string} [options.apiEndpoint='/api/activity-logs'] - Server endpoint for sending activity data
     * @param {boolean} [options.enabled=true] - Whether tracking is enabled
     * @param {number} [options.batchSize=10] - Number of activities to batch before sending
     * @param {number} [options.batchInterval=30000] - Interval in milliseconds for sending batched activities
     *
     * @example
     * const tracker = new ActivityTracker({
     *   apiEndpoint: '/api/custom-logs',
     *   enabled: true,
     *   batchSize: 5,
     *   batchInterval: 15000
     * });
     */
    constructor(options = {}) {
        this.apiEndpoint = options.apiEndpoint || (
            typeof window !== 'undefined' && !window.location.hostname.includes('localhost')
                ? 'https://api.clockwisecpa.app/api/activity-logs'
                : '/api/activity-logs'
        );
        this.sessionId = this._generateSessionId();
        this.userId = null;
        this.organizationId = null;
        this.enabled = options.enabled !== false; // Default to enabled
        this.batchSize = options.batchSize || 10;
        this.batchInterval = options.batchInterval || 30000; // 30 seconds
        
        // Activity queue for batching
        this.activityQueue = [];
        this.batchTimer = null;

        // Deduplication tracking
        this.lastActivity = null;
        
        // Performance tracking
        this.pageLoadStart = performance.now();
        this.navigationStart = performance.timing?.navigationStart || Date.now();
        
        // Navigation tracking state
        this.lastNavigationUrl = null;
        this.lastNavigationName = null;
        this.lastNavigationTime = 0;
        this.navigationCooldown = 2000; // 2 seconds minimum between navigation logs
        
        // Device and browser info
        this.deviceInfo = this._getDeviceInfo();
        this.browserInfo = this._getBrowserInfo();

        // Build version - fetch from version.json once
        this.buildVersion = null;
        this._loadBuildVersion();

        // Initialize tracking if enabled
        if (this.enabled) {
            this._initializeTracking();
        }
    }

    /**
     * Initialize all tracking mechanisms
     * Sets up event listeners for page, click, form, scroll, and unload tracking
     *
     * @private
     * @method _initializeTracking
     * @returns {void}
     */
    _initializeTracking() {
        this._setupPageTracking();
        this._setupClickTracking();
        this._setupFormTracking();
        this._setupScrollTracking();
        this._setupUnloadTracking();
        this._setupErrorTracking();
        this._startBatchTimer();

    }

    /**
     * Set user context for tracking
     * Updates the tracker with current user information and logs context changes
     *
     * @param {string|number} userId - The unique identifier for the user
     * @param {string|number|null} [organizationId=null] - The organization ID the user belongs to
     * @returns {void}
     *
     * @example
     * tracker.setUser('user123', 'org456');
     * tracker.setUser(123, null); // User without organization
     */
    setUser(userId, organizationId = null) {
        this.userId = userId;
        this.organizationId = organizationId;

        if (!userId) {
            console.error('ActivityTracker.setUser() called with NULL/undefined userId');
        }
    }

    /**
     * Track page view with comprehensive information
     * Records navigation events with performance metrics and duplicate detection
     *
     * @param {string} [url=window.location.href] - The URL of the page being viewed
     * @param {string} [title=document.title] - The title of the page
     * @param {string} [referrer=document.referrer] - The referrer URL
     * @returns {void}
     *
     * @example
     * tracker.trackPageView(); // Uses current page info
     * tracker.trackPageView('/dashboard', 'Dashboard', '/login');
     */
    trackPageView(url = window.location.href, title = document.title, referrer = document.referrer) {
        const currentTime = Date.now();
        
        // Generate descriptive navigation name first to check for duplicates
        const navigationName = this._getNavigationName(url, title);
        
        // Check if this is a duplicate navigation (same URL and name within cooldown period)
        if (url === this.lastNavigationUrl && 
            navigationName === this.lastNavigationName &&
            (currentTime - this.lastNavigationTime) < this.navigationCooldown) {
            return;
        }
        
        // Update navigation tracking state
        this.lastNavigationUrl = url;
        this.lastNavigationName = navigationName;
        this.lastNavigationTime = currentTime;
        
        const loadTime = performance.now() - this.pageLoadStart;
        
        const activity = {
            activity_type: 'navigation',
            activity_category: 'page_navigation',
            activity_name: navigationName,
            page_url: url,
            page_title: title,
            referrer_url: referrer || null,
            page_load_time: Math.round(loadTime),
            browser_info: this.browserInfo,
            device_info: this.deviceInfo,
            metadata: {
                viewport: {
                    width: window.innerWidth,
                    height: window.innerHeight
                },
                screen: {
                    width: window.screen.width,
                    height: window.screen.height
                },
                performance: this._getPerformanceMetrics()
            }
        };
        
        this._queueActivity(activity);
        
        // Reset page load timer for subsequent tracking
        this.pageLoadStart = performance.now();
    }

    /**
     * Track click events with detailed element information
     * Captures comprehensive data about clicked elements including position and modifiers
     *
     * @param {Event} event - The click event object
     * @returns {void}
     *
     * @example
     * document.addEventListener('click', (e) => {
     *   tracker.trackClick(e);
     * });
     */
    trackClick(event) {
        const element = event.target;
        const elementInfo = this._getElementInfo(element);
        const clickPosition = this._getClickPosition(event);
        
        const activity = {
            activity_type: 'click',
            activity_category: 'interaction',
            activity_name: `Clicked: ${elementInfo.text || elementInfo.selector || 'Unknown Element'}`,
            page_url: window.location.href,
            page_title: document.title,
            element_selector: elementInfo.selector,
            element_text: elementInfo.text,
            element_attributes: elementInfo.attributes,
            element_position: clickPosition,
            metadata: {
                timestamp: new Date().toISOString(),
                modifiers: {
                    ctrlKey: event.ctrlKey,
                    altKey: event.altKey,
                    shiftKey: event.shiftKey,
                    metaKey: event.metaKey
                }
            }
        };
        
        this._queueActivity(activity);
    }

    /**
     * Track form submissions
     * Records form submission events with sanitized form data
     *
     * @param {HTMLFormElement} form - The form element being submitted
     * @param {boolean} [success=true] - Whether the submission was successful
     * @param {string|Object|null} [errorDetails=null] - Error details if submission failed
     * @returns {void}
     *
     * @example
     * tracker.trackFormSubmit(formElement, true);
     * tracker.trackFormSubmit(formElement, false, 'Validation failed');
     */
    trackFormSubmit(form, success = true, errorDetails = null) {
        const formData = this._getFormData(form);
        const formName = form.name || form.id || form.className || 'unnamed-form';
        
        const activity = {
            activity_type: 'form_submit',
            activity_category: 'interaction',
            activity_name: `Form Submission: ${formName}${success ? '' : ' (Failed)'}`,
            page_url: window.location.href,
            page_title: document.title,
            metadata: {
                form_name: formName,
                form_data: formData,
                success: success,
                field_count: Object.keys(formData).length
            },
            error_details: errorDetails
        };
        
        this._queueActivity(activity);
    }

    /**
     * Track API calls
     * Records API request/response information for monitoring and debugging
     *
     * @param {string} endpoint - The API endpoint that was called
     * @param {string} method - The HTTP method used (GET, POST, PUT, DELETE, etc.)
     * @param {number} responseTime - The response time in milliseconds
     * @param {number} statusCode - The HTTP status code returned
     * @param {string|Object|null} [errorDetails=null] - Error details if the request failed
     * @returns {void}
     *
     * @example
     * tracker.trackApiCall('/api/users', 'GET', 245, 200);
     * tracker.trackApiCall('/api/data', 'POST', 1200, 500, 'Server error');
     */
    trackApiCall(endpoint, method, responseTime, statusCode, errorDetails = null) {
        const activity = {
            activity_type: 'api_call',
            activity_category: 'system',
            activity_name: `API Call: ${method} ${endpoint}${statusCode >= 400 ? ` (Error: ${statusCode})` : ''}`,
            page_url: window.location.href,
            response_time: responseTime,
            metadata: {
                endpoint: endpoint,
                method: method,
                status_code: statusCode
            },
            error_details: errorDetails
        };
        
        this._queueActivity(activity);
    }

    /**
     * Track system events
     * Records custom system events with associated data
     *
     * @param {string} eventName - A descriptive name for the system event
     * @param {Object} [eventData={}] - Additional data associated with the event
     * @returns {void}
     *
     * @example
     * tracker.logSystemEvent('user_login', { method: 'oauth' });
     * tracker.logSystemEvent('feature_used', { feature: 'export', format: 'pdf' });
     */
    logSystemEvent(eventName, eventData = {}) {
        const activity = {
            activity_type: 'system_event',
            activity_category: 'system',
            activity_name: eventName,
            page_url: window.location.href,
            page_title: document.title,
            metadata: {
                ...eventData,
                timestamp: new Date().toISOString()
            }
        };
        
        this._queueActivity(activity);
    }

    /**
     * Track scroll events (throttled)
     * Records user scrolling behavior with position information
     *
     * @returns {void}
     * @example
     * // Called automatically when scroll tracking is enabled
     * // Can also be called manually:
     * tracker.trackScroll();
     */
    trackScroll() {
        if (this.scrollTimeout) return;
        
        this.scrollTimeout = setTimeout(() => {
            const scrollPercentage = Math.round(
                (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
            );
            
            const activity = {
                activity_type: 'scroll',
                activity_category: 'interaction',
                activity_name: `Scrolled to ${scrollPercentage}%`,
                page_url: window.location.href,
                page_title: document.title,
                referrer: document.referrer,

                // Enhanced tracking data for modal display
                user_agent: navigator.userAgent,
                ip_address: null, // Will be set by server
                language: navigator.language,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                screen_width: window.screen.width,
                screen_height: window.screen.height,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
                color_depth: window.screen.colorDepth,

                metadata: {
                    scroll_percentage: scrollPercentage,
                    scroll_position: window.scrollY,
                    page_height: document.body.scrollHeight
                }
            };
            
            this._queueActivity(activity);
            this.scrollTimeout = null;
        }, 1000); // Throttle to 1 second
    }

    /**
     * Set up page tracking listeners
     * Configures mutation observers and event listeners for detecting page navigation
     *
     * @private
     * @returns {void}
     */
    _setupPageTracking() {
        // Track initial page load
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => this.trackPageView(), 100);
            });
        } else {
            setTimeout(() => this.trackPageView(), 100);
        }

        // Track navigation changes (for SPAs) - use more reliable method
        let lastUrl = window.location.href;
        let lastTitle = document.title; // eslint-disable-line no-unused-vars
        
        // Check for URL changes more efficiently
        const checkUrlChange = () => {
            const currentUrl = window.location.href;
            const currentTitle = document.title;
            
            if (currentUrl !== lastUrl) {
                lastUrl = currentUrl;
                lastTitle = currentTitle;
                setTimeout(() => this.trackPageView(), 100);
            }
        };

        // Use a more targeted approach to detect navigation changes
        // Only watch for specific changes that indicate navigation
        const observer = new MutationObserver((mutations) => {
            let shouldCheck = false;
            
            for (const mutation of mutations) {
                // Only check if title changed or if significant DOM changes occurred
                if (mutation.type === 'childList' && mutation.target === document.head) {
                    // Title or other head elements changed
                    shouldCheck = true;
                    break;
                } else if (mutation.type === 'childList' && 
                          mutation.target.tagName === 'BODY' &&
                          mutation.addedNodes.length > 0) {
                    // Major body content changes (potential page navigation)
                    shouldCheck = true;
                    break;
                }
            }
            
            if (shouldCheck) {
                checkUrlChange();
            }
        });
        
        // Observe only specific changes that indicate navigation
        observer.observe(document, { 
            subtree: true, 
            childList: true,
            attributes: false // Don't watch attribute changes to reduce noise
        });

        // Track browser navigation (back/forward buttons)
        window.addEventListener('popstate', () => {
            setTimeout(() => this.trackPageView(), 100);
        });
        
        // Track hash changes
        window.addEventListener('hashchange', () => {
            setTimeout(() => this.trackPageView(), 100);
        });
    }

    /**
     * Set up click tracking
     * Adds global click event listener with tracking exclusion support
     *
     * @private
     * @returns {void}
     */
    _setupClickTracking() {
        document.addEventListener('click', (event) => {
            // Skip tracking clicks on tracking-disabled elements
            if (event.target.closest('[data-no-tracking]')) {
                return;
            }
            this.trackClick(event);
        }, true);
    }

    /**
     * Set up form tracking
     * Adds global form submission listener with tracking exclusion support
     *
     * @private
     * @returns {void}
     */
    _setupFormTracking() {
        document.addEventListener('submit', (event) => {
            const form = event.target;
            if (form.tagName === 'FORM' && !form.hasAttribute('data-no-tracking')) {
                this.trackFormSubmit(form, true);
            }
        }, true);
    }

    /**
     * Set up scroll tracking
     * Adds throttled scroll event listener to track user scrolling behavior
     *
     * @private
     * @returns {void}
     */
    _setupScrollTracking() {
        let scrollTimer = null;
        window.addEventListener('scroll', () => {
            if (scrollTimer) {
                clearTimeout(scrollTimer);
            }
            scrollTimer = setTimeout(() => {
                this.trackScroll();
            }, 200);
        });
    }

    /**
     * Set up page unload tracking
     * Adds beforeunload listener to flush remaining activities and log page unload
     *
     * @private
     * @returns {void}
     */
    _setupUnloadTracking() {
        window.addEventListener('beforeunload', () => {
            // Log page unload BEFORE flushing so it's included in the batch
            this.logSystemEvent('page_unload', {
                page_url: window.location.href,
                time_on_page: Date.now() - this.navigationStart
            });

            // Send any remaining activities
            this._flushQueue(true);
        });
    }

    /**
     * Set up global error tracking
     * Captures uncaught errors, unhandled promise rejections, and console.error calls
     *
     * @private
     * @returns {void}
     */
    _setupErrorTracking() {
        // Track uncaught JS errors
        window.addEventListener('error', (event) => {
            this._trackError({
                error_type: 'uncaught_error',
                message: event.message || 'Unknown error',
                filename: event.filename || null,
                lineno: event.lineno || null,
                colno: event.colno || null,
                stack: event.error?.stack || null
            });
        });

        // Track unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            const reason = event.reason;
            let message = 'Unhandled promise rejection';
            let stack = null;

            if (reason instanceof Error) {
                message = reason.message;
                stack = reason.stack;
            } else if (typeof reason === 'string') {
                message = reason;
            } else if (reason) {
                try { message = JSON.stringify(reason); } catch { message = String(reason); }
            }

            this._trackError({
                error_type: 'unhandled_rejection',
                message,
                stack
            });
        });

        // Override console.error to capture all logged errors
        this._originalConsoleError = console.error;
        const originalConsoleError = this._originalConsoleError;
        const tracker = this;
        console.error = function (...args) {
            originalConsoleError.apply(console, args);

            // Don't track our own ActivityTracker warnings to avoid loops
            const firstArg = typeof args[0] === 'string' ? args[0] : '';
            if (firstArg.includes('ActivityTracker')) return;

            let message;
            try {
                message = args.map(arg => {
                    if (arg instanceof Error) return arg.stack || arg.message;
                    if (typeof arg === 'string') return arg;
                    try { return JSON.stringify(arg); } catch { return String(arg); }
                }).join(' ');
            } catch {
                message = 'console.error (could not serialize)';
            }

            // Truncate very long messages
            if (message.length > 2000) message = message.substring(0, 2000) + '...';

            tracker._trackError({
                error_type: 'console_error',
                message,
                stack: args.find(a => a instanceof Error)?.stack || null
            });
        };
    }

    /**
     * Queue an error activity
     *
     * @private
     * @param {Object} errorInfo - Error details
     * @param {string} errorInfo.error_type - Type of error (uncaught_error, unhandled_rejection, console_error)
     * @param {string} errorInfo.message - Error message
     * @param {string|null} [errorInfo.filename] - Source file
     * @param {number|null} [errorInfo.lineno] - Line number
     * @param {number|null} [errorInfo.colno] - Column number
     * @param {string|null} [errorInfo.stack] - Stack trace
     */
    _trackError(errorInfo) {
        const activity = {
            activity_type: 'error',
            activity_category: 'error',
            activity_name: `${errorInfo.error_type}: ${errorInfo.message?.substring(0, 200) || 'Unknown'}`,
            page_url: window.location.href,
            page_title: document.title,
            browser_info: this.browserInfo,
            device_info: this.deviceInfo,
            metadata: {
                error_type: errorInfo.error_type,
                message: errorInfo.message,
                filename: errorInfo.filename || null,
                lineno: errorInfo.lineno || null,
                colno: errorInfo.colno || null,
                stack: errorInfo.stack || null,
                timestamp: new Date().toISOString()
            }
        };

        this._queueActivity(activity);
    }

    /**
     * Generate unique session ID
     * Creates a unique identifier for the current tracking session
     *
     * @private
     * @returns {string} A unique session identifier
     * @example
     * // Returns something like: "session_1640995200000_abc123def"
     */
    _generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    /**
     * Get device information
     * Collects comprehensive device and viewport information
     *
     * @private
     * @returns {Object} Device information object
     * @returns {number} returns.screen_width - Screen width in pixels
     * @returns {number} returns.screen_height - Screen height in pixels
     * @returns {number} returns.viewport_width - Viewport width in pixels
     * @returns {number} returns.viewport_height - Viewport height in pixels
     * @returns {number} returns.device_pixel_ratio - Device pixel ratio
     * @returns {boolean} returns.touch_support - Whether device supports touch
     * @returns {string} returns.platform - Operating system platform
     * @returns {string} returns.language - User's preferred language
     */
    _getDeviceInfo() {
        return {
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            device_pixel_ratio: window.devicePixelRatio || 1,
            touch_support: 'ontouchstart' in window,
            platform: navigator.platform,
            language: navigator.language
        };
    }

    /**
     * Get browser information
     * Collects comprehensive browser and environment information
     *
     * @private
     * @returns {Object} Browser information object
     * @returns {string} returns.user_agent - Full user agent string
     * @returns {string} returns.browser_name - Detected browser name
     * @returns {string} returns.browser_version - Detected browser version
     * @returns {boolean} returns.cookies_enabled - Whether cookies are enabled
     * @returns {boolean} returns.online - Whether browser is online
     * @returns {string} returns.timezone - User's timezone
     */
    _getBrowserInfo() {
        const ua = navigator.userAgent;
        return {
            user_agent: ua,
            browser_name: this._getBrowserName(ua),
            browser_version: this._getBrowserVersion(ua),
            cookies_enabled: navigator.cookieEnabled,
            online: navigator.onLine,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }

    _loadBuildVersion() {
        fetch('/version.json?t=' + Date.now())
            .then(r => r.json())
            .then(data => { this.buildVersion = data.version || null; })
            .catch(() => {});
    }

    /**
     * Get browser name from user agent
     * Parses user agent string to identify browser
     *
     * @private
     * @param {string} ua - User agent string
     * @returns {string} Browser name (Chrome, Firefox, Safari, Edge, Opera, or Unknown)
     */
    _getBrowserName(ua) {
        if (ua.includes('Chrome')) return 'Chrome';
        if (ua.includes('Firefox')) return 'Firefox';
        if (ua.includes('Safari')) return 'Safari';
        if (ua.includes('Edge')) return 'Edge';
        if (ua.includes('Opera')) return 'Opera';
        return 'Unknown';
    }

    /**
     * Get browser version from user agent
     * Extracts version number from user agent string
     *
     * @private
     * @param {string} ua - User agent string
     * @returns {string} Browser version or 'Unknown'
     */
    _getBrowserVersion(ua) {
        const matches = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/([0-9]+)/);
        return matches ? matches[2] : 'Unknown';
    }

    /**
     * Get detailed element information
     * Extracts comprehensive information about a DOM element
     *
     * @private
     * @param {HTMLElement} element - The DOM element to analyze
     * @returns {Object} Element information object
     * @returns {string} returns.selector - CSS selector for the element
     * @returns {string} returns.text - Text content of the element (truncated)
     * @returns {Object} returns.attributes - Relevant element attributes
     * @returns {string} returns.tag_name - Element tag name in lowercase
     * @returns {string|null} returns.element_id - Element ID if present
     * @returns {string|null} returns.class_names - Element class names
     */
    _getElementInfo(element) {
        const selector = this._getElementSelector(element);
        const text = this._getElementText(element);
        const attributes = this._getElementAttributes(element);
        
        return {
            selector,
            text,
            attributes,
            tag_name: element.tagName.toLowerCase(),
            element_id: element.id || null,
            class_names: (() => {
                if (!element.className) return null;
                // Handle different types of className (string, SVGAnimatedString, etc.)
                const classNames = typeof element.className === 'string' 
                    ? element.className 
                    : element.className.baseVal || element.className.toString();
                return classNames && classNames.trim() ? classNames.trim() : null;
            })()
        };
    }

    /**
     * Generate CSS selector for element
     * Creates a CSS selector path to uniquely identify an element
     *
     * @private
     * @param {HTMLElement} element - The DOM element
     * @returns {string} CSS selector path
     * @example
     * // Returns something like: "#myId" or "div.class1.class2 > span"
     */
    _getElementSelector(element) {
        if (element.id) {
            return `#${element.id}`;
        }
        
        const path = [];
        while (element && element.nodeType === Node.ELEMENT_NODE) {
            let selector = element.nodeName.toLowerCase();
            if (element.className) {
                // Handle different types of className (string, SVGAnimatedString, etc.)
                const classNames = typeof element.className === 'string' 
                    ? element.className 
                    : element.className.baseVal || element.className.toString();
                if (classNames && classNames.trim()) {
                    selector += '.' + classNames.trim().split(/\s+/).join('.');
                }
            }
            path.unshift(selector);
            element = element.parentNode;
            
            // Stop at body to avoid very long selectors
            if (element && element.nodeName.toLowerCase() === 'body') {
                break;
            }
        }
        
        return path.join(' > ');
    }

    /**
     * Get element text content (truncated)
     * Extracts and truncates element text content for tracking
     *
     * @private
     * @param {HTMLElement} element - The DOM element
     * @returns {string} Truncated text content (max 100 characters)
     */
    _getElementText(element) {
        const text = element.textContent || element.innerText || '';
        return text.trim().substring(0, 100); // Limit to 100 chars
    }

    /**
     * Get relevant element attributes
     * Extracts important attributes for tracking purposes
     *
     * @private
     * @param {HTMLElement} element - The DOM element
     * @returns {Object} Object containing relevant attribute key-value pairs
     */
    _getElementAttributes(element) {
        const relevantAttrs = ['href', 'src', 'alt', 'title', 'role', 'aria-label', 'data-*'];
        const attributes = {};
        
        for (let attr of element.attributes) {
            const name = attr.name.toLowerCase();
            if (relevantAttrs.some(relevant => 
                name === relevant || (relevant.endsWith('*') && name.startsWith(relevant.slice(0, -1)))
            )) {
                attributes[name] = attr.value;
            }
        }
        
        return attributes;
    }

    /**
     * Get click position information
     * Extracts detailed position data from a click event
     *
     * @private
     * @param {Event} event - The click event
     * @returns {Object} Position information object
     * @returns {number} returns.click_x - X coordinate of click
     * @returns {number} returns.click_y - Y coordinate of click
     * @returns {number} returns.element_x - X position of element
     * @returns {number} returns.element_y - Y position of element
     * @returns {number} returns.element_width - Width of element
     * @returns {number} returns.element_height - Height of element
     */
    _getClickPosition(event) {
        const rect = event.target.getBoundingClientRect();
        return {
            click_x: event.clientX,
            click_y: event.clientY,
            element_x: rect.left,
            element_y: rect.top,
            element_width: rect.width,
            element_height: rect.height
        };
    }

    /**
     * Get sanitized form data
     * Extracts form data while sanitizing sensitive information
     *
     * @private
     * @param {HTMLFormElement} form - The form element
     * @returns {Object} Sanitized form data object with field names as keys
     */
    _getFormData(form) {
        const formData = {};
        const sensitiveFields = ['password', 'ssn', 'social', 'credit_card', 'cvv', 'pin'];
        
        for (let element of form.elements) {
            if (element.name) {
                const fieldName = element.name.toLowerCase();
                const isSensitive = sensitiveFields.some(sensitive => fieldName.includes(sensitive));
                
                if (isSensitive) {
                    formData[element.name] = '***REDACTED***';
                } else if (element.type === 'checkbox' || element.type === 'radio') {
                    formData[element.name] = element.checked;
                } else if (element.value) {
                    // Truncate long values
                    formData[element.name] = element.value.substring(0, 100);
                }
            }
        }
        
        return formData;
    }

    /**
     * Get performance metrics
     * Collects browser performance timing information
     *
     * @private
     * @returns {Object|null} Performance metrics object or null if not available
     * @returns {number} returns.dns_lookup - DNS lookup time in milliseconds
     * @returns {number} returns.tcp_connect - TCP connection time in milliseconds
     * @returns {number} returns.server_response - Server response time in milliseconds
     * @returns {number} returns.dom_processing - DOM processing time in milliseconds
     * @returns {number} returns.total_load_time - Total page load time in milliseconds
     */
    _getPerformanceMetrics() {
        if (!performance.timing) return null;
        
        const timing = performance.timing;
        return {
            dns_lookup: timing.domainLookupEnd - timing.domainLookupStart,
            tcp_connect: timing.connectEnd - timing.connectStart,
            server_response: timing.responseEnd - timing.requestStart,
            dom_processing: timing.domComplete - timing.domLoading,
            total_load_time: timing.loadEventEnd - timing.navigationStart
        };
    }

    /**
     * Queue activity for batch sending
     * Adds activity to the batch queue and sends if queue is full
     *
     * @private
     * @param {Object} activity - The activity object to queue
     * @returns {void}
     */
    _queueActivity(activity) {
        if (!this.enabled) {
            return;
        }

        // Skip ALL activity logging if no user is set
        if (!this.userId) {
            console.warn('ActivityTracker: Attempting to log activity without user context');
            return;
        }

        // Check for duplicate activities within a short time window (prevents rapid-fire duplicates)
        const now = Date.now();
        const activityKey = `${activity.activity_type}_${activity.activity_name}_${activity.page_url}`;

        if (this.lastActivity &&
            this.lastActivity.key === activityKey &&
            (now - this.lastActivity.timestamp) < 500) { // 500ms deduplication window
            // console.log('Duplicate activity detected, skipping:', activityKey);
            return;
        }

        this.lastActivity = { key: activityKey, timestamp: now };

        // Add common fields - user_id will be extracted from JWT on server
        activity.session_id = this.sessionId;
        activity.organization_id = this.organizationId;
        activity.ip_address = null; // Will be set by server
        activity.client_timestamp = new Date().toISOString();
        activity.build_version = this.buildVersion;

        this.activityQueue.push(activity);

        // Send immediately if queue is full
        if (this.activityQueue.length >= this.batchSize) {
            this._flushQueue();
        }
    }

    /**
     * Start batch timer
     * Initializes the interval timer for automatic batch sending
     *
     * @private
     * @returns {void}
     */
    _startBatchTimer() {
        if (this.batchTimer) {
            clearInterval(this.batchTimer);
        }
        
        this.batchTimer = setInterval(() => {
            if (this.activityQueue.length > 0) {
                this._flushQueue();
            }
        }, this.batchInterval);
    }

    /**
     * Flush activity queue to server
     * Sends all queued activities to the server endpoint
     *
     * @private
     * @param {boolean} [synchronous=false] - Whether to use synchronous sending (for page unload)
     * @returns {Promise<void>} Promise that resolves when activities are sent
     */
    async _flushQueue(synchronous = false) {
        if (this.activityQueue.length === 0) {
            return;
        }

        // Don't send activities if user is not authenticated
        if (!this.userId) {
            console.warn('ActivityTracker: Skipping flush - no user context');
            this.activityQueue = []; // Clear the queue to prevent retry
            return;
        }

        const activities = [...this.activityQueue];
        this.activityQueue = [];

        const payload = {
            activities: activities,
            batch_info: {
                batch_size: activities.length,
                timestamp: new Date().toISOString(),
                session_id: this.sessionId
            }
        };


        
        try {
            // Get CSRF token from cookie (same pattern as other API calls)
            const getCookie = (name) => {
                const value = `; ${document.cookie}`;
                const parts = value.split(`; ${name}=`);
                if (parts.length === 2) return parts.pop().split(';').shift();
                return '';
            };

            const csrfToken = getCookie('csrf_access_token');
            const accessToken = getCookie('access_token_cookie'); // eslint-disable-line no-unused-vars

            const options = {
                method: 'POST',
                credentials: 'include', // Include cookies for JWT authentication
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken, // Include CSRF token like other API calls
                },
                body: JSON.stringify(payload)
            };

            
            if (synchronous) {
                // Use fetch with keepalive for page unload - survives page close
                // and includes cookies + CSRF headers (unlike sendBeacon)
                try {
                    fetch(this.apiEndpoint, { ...options, keepalive: true });
                } catch (e) {
                    // Fallback to sendBeacon if fetch+keepalive fails
                    const blob = new Blob([JSON.stringify(payload)], {
                        type: 'application/json'
                    });
                    navigator.sendBeacon(this.apiEndpoint, blob);
                }
            } else {
                const response = await fetch(this.apiEndpoint, options);

                if (!response.ok) {
                    const errorDetails = {
                        status: response.status,
                        statusText: response.statusText,
                        url: response.url,
                        activitiesCount: activities.length
                    };

                    // Try to get response text for more details
                    try {
                        const responseText = await response.text();
                        errorDetails.responseBody = responseText;
                    } catch (textError) {
                        errorDetails.responseBody = 'Could not read response body';
                    }

                    console.error('❌ ActivityTracker: Failed to send activity data:', errorDetails);

                    // Handle different error types
                    if (response.status === 401) {
                        this._handleAuthenticationError();
                    } else if (response.status === 400) {
                        // Don't re-queue on 400 errors as the data is likely invalid
                    } else if (response.status >= 500) {
                        // Re-queue on server errors for retry
                        this.activityQueue.unshift(...activities);
                    }
                }
            }
        } catch (error) {
            console.error('Network error sending activity data:', error.message);

            // Re-queue activities on network error for retry
            this.activityQueue.unshift(...activities);
        }
    }

    /**
     * Handle authentication errors
     * Manages authentication failures during activity sending
     *
     * @private
     * @returns {void}
     */
    _handleAuthenticationError() {
        // Clear the queue to prevent retrying auth-failed activities
        this.activityQueue = [];

        // You could emit a custom event here for the app to handle
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('activityTrackerAuthError', {
                detail: { message: 'Authentication failed for activity tracking' }
            }));
        }
    }

    /**
     * Enable tracking
     * Activates the activity tracker and initializes all tracking mechanisms
     *
     * @returns {void}
     * @example
     * tracker.enable();
     */
    enable() {
        this.enabled = true;
        this._initializeTracking();
    }

    /**
     * Disable tracking
     * Deactivates the activity tracker and clears all timers and queues
     *
     * @returns {void}
     * @example
     * tracker.disable();
     */
    disable() {
        this.enabled = false;
        if (this.batchTimer) {
            clearInterval(this.batchTimer);
            this.batchTimer = null;
        }
        this.activityQueue = [];
    }

    /**
     * Flush remaining activities and cleanup
     * Performs final cleanup, sending any remaining activities and disabling tracking
     *
     * @returns {void}
     * @example
     * tracker.destroy(); // Call before removing tracker instance
     */
    destroy() {
        this._flushQueue(true);
        if (this._originalConsoleError) {
            console.error = this._originalConsoleError;
        }
        this.disable();
    }

    /**
     * Generate descriptive navigation name based on URL and title
     * Creates human-readable navigation names for activity tracking
     *
     * @private
     * @param {string} url - The URL being navigated to
     * @param {string} title - The page title
     * @returns {string} Descriptive navigation name
     * @example
     * // Returns: "navigated to client details"
     * _getNavigationName('/clients/123', 'Client Details - Clockwise CPA')
     */
    _getNavigationName(url, title) {
        try {
            const urlObj = new URL(url);
            const path = urlObj.pathname;
            
            // Comprehensive page mappings based on actual routes
            const pageMap = {
                // Main pages
                '/': 'navigated to dashboard',
                '/dashboard': 'navigated to dashboard',
                
                // Client routes
                '/clients/manage': 'navigated to client management',
                '/clients/create': 'navigated to new client form',
                
                // User routes
                '/users/manage': 'navigated to user management',
                '/users/create': 'navigated to new user form',
                '/profile': 'navigated to profile',
                '/settings': 'navigated to settings',

                // Time entry routes
                '/time_entries/create': 'navigated to new time entry',
                
                // Invoice routes
                '/billing': 'navigated to billing',
                '/reports': 'navigated to reports',
                '/ledger': 'navigated to ar ledger',
                '/invoice_texts/manage': 'navigated to invoice text management',
                '/invoice_texts/create': 'navigated to new invoice text',
                
                // Admin routes
                '/admin': 'navigated to admin panel',
                '/admin/roles': 'navigated to role management',
                '/activity-types': 'navigated to activity types',
                
                // Support routes
                '/support-tickets': 'navigated to support tickets',
                '/support-tickets/create': 'navigated to new support ticket',
                
                // Auth routes
                '/login': 'navigated to login',
                '/access-denied': 'navigated to access denied',
                '/error': 'navigated to error page'
            };
            
            // Check for exact path match first
            if (pageMap[path]) {
                return pageMap[path];
            }
            
            // Check for dynamic routes (e.g., /clients/123, /invoices/456/edit)
            const pathSegments = path.split('/').filter(Boolean);
            if (pathSegments.length >= 2) {
                const baseRoute = pathSegments[0];
                const secondSegment = pathSegments[1];
                const thirdSegment = pathSegments[2];
                
                // Handle specific dynamic route patterns
                if (baseRoute === 'clients') {
                    if (/^\d+$/.test(secondSegment)) {
                        if (thirdSegment === 'edit') return 'navigated to edit client';
                        if (thirdSegment === 'wip') return 'navigated to client wip queue';
                        if (thirdSegment === 'activity-types') return 'navigated to client activity types';
                        if (thirdSegment === 'payments') return 'navigated to client payments';
                        return 'navigated to client details';
                    }
                } else if (baseRoute === 'invoices') {
                    if (/^\d+$/.test(secondSegment)) {
                        if (thirdSegment === 'edit') return 'navigated to edit invoice';
                        return 'navigated to invoice details';
                    }
                } else if (baseRoute === 'users') {
                    if (/^\d+$/.test(secondSegment) && thirdSegment === 'edit') {
                        return 'navigated to edit user';
                    }
                } else if (baseRoute === 'time_entries') {
                    if (/^\d+$/.test(secondSegment) && thirdSegment === 'edit') {
                        return 'navigated to edit time entry';
                    }
                } else if (baseRoute === 'payments') {
                    if (/^\d+$/.test(secondSegment) && thirdSegment === 'edit') {
                        return 'navigated to edit payment';
                    }
                } else if (baseRoute === 'invoice_texts') {
                    if (/^\d+$/.test(secondSegment) && thirdSegment === 'edit') {
                        return 'navigated to edit invoice text';
                    }
                } else if (baseRoute === 'support-tickets') {
                    if (/^\d+$/.test(secondSegment)) {
                        return 'navigated to support ticket details';
                    }
                } else if (baseRoute === 'reset_password') {
                    return 'navigated to reset password';
                } else if (baseRoute === 'billing' && /^\d+$/.test(secondSegment)) {
                    return 'navigated to billing (refreshed)';
                }
            }
            
            // Fallback to title-based naming (filter out generic site titles)
            if (title && title.trim()) {
                const lowerTitle = title.toLowerCase();
                // NEVER use generic site titles - skip them entirely
                if (lowerTitle.includes('clockwise') || lowerTitle.includes('cpa')) {
                    // Skip to path-based fallback instead of using generic site title
                } else if (lowerTitle.length > 3) {
                    return `navigated to ${lowerTitle}`;
                }
                
                // Try to extract specific page name from title
                // Handle formats like "Clients - Clockwise CPA" or "Clockwise CPA - Clients"
                const titleParts = title.split(/[-|•]/).map(part => part.trim());
                for (const part of titleParts) {
                    const cleanPart = part.toLowerCase();
                    if (!cleanPart.includes('clockwise') && 
                        !cleanPart.includes('cpa') && 
                        cleanPart.length > 2) {
                        return `navigated to ${cleanPart}`;
                    }
                }
            }
            
            // Final fallback to path-based naming
            if (pathSegments.length > 0) {
                const lastSegment = pathSegments[pathSegments.length - 1];
                // Clean up the segment name
                const cleanSegment = lastSegment.replace(/[-_]/g, ' ').toLowerCase();
                return `navigated to ${cleanSegment}`;
            }
            
            return 'navigated to page';
            
        } catch (error) {
            // If URL parsing fails, use title or generic name
            if (title && title.trim()) {
                const lowerTitle = title.toLowerCase();
                // NEVER use generic site titles - skip them entirely
                if (lowerTitle.includes('clockwise') || lowerTitle.includes('cpa')) {
                    // Skip to final fallback instead of using generic site title
                } else if (lowerTitle.length > 3) {
                    return `navigated to ${lowerTitle}`;
                }
                
                // Try to extract specific page name from title
                const titleParts = title.split(/[-|•]/).map(part => part.trim());
                for (const part of titleParts) {
                    const cleanPart = part.toLowerCase();
                    if (!cleanPart.includes('clockwise') && 
                        !cleanPart.includes('cpa') && 
                        cleanPart.length > 2) {
                        return `navigated to ${cleanPart}`;
                    }
                }
            }
            return 'navigated to page';
        }
    }
}

// Export for use
export default ActivityTracker;

// Global instance for easy access
let globalTracker = null;

/**
 * Initialize the global activity tracker instance
 * Creates a singleton instance of ActivityTracker for application-wide use
 *
 * @param {Object} [options={}] - Configuration options for the tracker
 * @returns {ActivityTracker} The global tracker instance
 *
 * @example
 * const tracker = initializeGlobalTracker({
 *   apiEndpoint: '/api/activity-logs',
 *   enabled: true,
 *   batchSize: 10
 * });
 */
export const initializeGlobalTracker = (options = {}) => {
    if (!globalTracker) {
        globalTracker = new ActivityTracker(options);
    }
    return globalTracker;
};

/**
 * Get the global activity tracker instance
 * Returns the current global tracker instance or null if not initialized
 *
 * @returns {ActivityTracker|null} The global tracker instance or null
 *
 * @example
 * const tracker = getGlobalTracker();
 * if (tracker) {
 *   tracker.trackPageView();
 * }
 */
export const getGlobalTracker = () => globalTracker;

// Utility functions for common tracking scenarios

/**
 * Track a page view using the global tracker
 * Convenience function for tracking page views without accessing the global instance directly
 *
 * @param {string} [url] - The URL to track
 * @param {string} [title] - The page title
 * @param {string} [referrer] - The referrer URL
 * @returns {void}
 *
 * @example
 * trackPageView('/dashboard', 'Dashboard', '/login');
 */
export const trackPageView = (url, title, referrer) => {
    if (globalTracker) globalTracker.trackPageView(url, title, referrer);
};

/**
 * Track a click event using the global tracker
 * Convenience function for tracking clicks without accessing the global instance directly
 *
 * @param {Event} event - The click event
 * @returns {void}
 *
 * @example
 * document.addEventListener('click', trackClick);
 */
export const trackClick = (event) => {
    if (globalTracker) globalTracker.trackClick(event);
};

/**
 * Track a form submission using the global tracker
 * Convenience function for tracking form submissions
 *
 * @param {HTMLFormElement} form - The form element
 * @param {boolean} [success] - Whether the submission was successful
 * @param {string|Object} [errorDetails] - Error details if submission failed
 * @returns {void}
 *
 * @example
 * trackFormSubmit(formElement, true);
 * trackFormSubmit(formElement, false, 'Validation error');
 */
export const trackFormSubmit = (form, success, errorDetails) => {
    if (globalTracker) globalTracker.trackFormSubmit(form, success, errorDetails);
};

/**
 * Track an API call using the global tracker
 * Convenience function for tracking API calls
 *
 * @param {string} endpoint - The API endpoint
 * @param {string} method - The HTTP method
 * @param {number} responseTime - Response time in milliseconds
 * @param {number} statusCode - HTTP status code
 * @param {string|Object} [errorDetails] - Error details if the call failed
 * @returns {void}
 *
 * @example
 * trackApiCall('/api/users', 'GET', 245, 200);
 * trackApiCall('/api/data', 'POST', 1200, 500, 'Server error');
 */
export const trackApiCall = (endpoint, method, responseTime, statusCode, errorDetails) => {
    if (globalTracker) globalTracker.trackApiCall(endpoint, method, responseTime, statusCode, errorDetails);
};

/**
 * Log a system event using the global tracker
 * Convenience function for logging custom system events
 *
 * @param {string} eventName - The event name
 * @param {Object} [eventData] - Additional event data
 * @returns {void}
 *
 * @example
 * logSystemEvent('user_login', { method: 'oauth' });
 * logSystemEvent('feature_used', { feature: 'export' });
 */
export const logSystemEvent = (eventName, eventData) => {
    if (globalTracker) globalTracker.logSystemEvent(eventName, eventData);
};